import {
  ChangeEventHandler,
  useEffect,
  useMemo,
  useState,
  FocusEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import {
  BREAKDOWN_SEMANTIC_CODE,
  DAYS_OF_WEEK,
  MULTIPLE_FILTERING,
  SINGLE_FILTERING,
} from "./utils"
import { BaseRateRowRecord, Dimension, DimensionOption } from "models/ratecard"
import {
  Cable,
  Column,
  WritableCellRenderProps,
  useCell,
  useCellValue,
  useRow,
} from "components/Cable"
import { IOption, SelectedOptionChanges } from "ui/inputs/GhostSelect"
import {
  createBaseRateRow,
  deleteBaseRateRow,
  updateBaseRateRow,
} from "api/ratecard"
import { css, cx } from "@emotion/css"
//
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"
import { queryCache, useMutation, useQuery } from "react-query"

import AutoCompleteInput from "components/inputs/AutoComplete"
import { BaseRateFilters } from "./BaseRateFilters"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { HourInterval } from "models/packages"
import { HourRangePicker } from "components/HourPicker/HourRangePicker"
import Input from "ui/inputs/Input"
import { MarketTarget } from "models/targets"
import MoreActions from "components/MoreActions"
//
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { TextInput } from "components/inputs/TextInput"
import { fetchMarketTargets } from "api/targets"
import { hourLabelFromTime } from "components/HourPicker"
import lget from "lodash/get"
import setW from "lodash/setWith"
import { useAuthenticated } from "providers/A12n"
import { useColumnKeys } from "components/Cable/atoms"
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"

type RatesTableProps = {
  baseRateId: string
  dimensions: Dimension[]
  data: BaseRateRowRecord[]
  getErrors?: (errors?: ErrorsMap) => void
}

export function RatesTable(props: RatesTableProps) {
  const { baseRateId, dimensions, data } = props
  const { t } = useI18n()
  const { meta, hasAuthority } = useAuthenticated()
  const marketId = meta?.layerId ?? ""
  const [open, setOpen] = useState<boolean>(false)
  const [activeRow, setActiveRow] = useState<string>()
  const [filterState, setFilterState] =
    useState<Record<string, { label: string; value: string }[]>>()
  const [timebelt, setTimebelt] = useState<HourInterval>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const dmks = dimensions.map((x) => x.mediaFieldSemanticCode).join("/")
  const getErrors = (errors?: ErrorsMap) => setErrors(errors)

  const { data: targets, updatedAt } = useQuery(
    ["targets", marketId],
    fetchMarketTargets,
  )

  const handleOpen = () => {
    setActiveRow(undefined)
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const onDuplicate = (id?: string) => {
    setOpen(true)
    setActiveRow(id)
  }

  const allowed = hasAuthority([
    "AUTH_CREATE_RATE_CARD",
    "AUTH_EDIT_RATE_CARD",
    "AUTH_DELETE_RATE_CARD",
  ])

  const columns = useMemo(
    () =>
      createColumns(
        dimensions,
        baseRateId,
        t,
        allowed,
        onDuplicate,
        getErrors,
        targets,
      ),
    // eslint-disable-next-line
    [dmks, baseRateId, updatedAt],
  )

  //** filters **//

  const hasFilterOn = (keys: string[], d?: Record<string, any>) =>
    !!d &&
    !!Object.keys(d)
      ?.filter((k) => keys.includes(k))
      ?.some((k) => !!d?.[k]?.length)

  const filtered_single = hasFilterOn(SINGLE_FILTERING, filterState)
    ? data?.filter((d) =>
        Object.keys(d)
          ?.filter(
            (k) =>
              !!filterState?.[k]?.length &&
              Object.keys(filterState ?? []).includes(k) &&
              SINGLE_FILTERING.includes(k),
          )
          ?.every((k) =>
            filterState?.[k]?.map((s) => s?.value)?.includes(d?.[k] as string),
          ),
      )
    : data

  const filtered_multiple =
    !!filterState && hasFilterOn(MULTIPLE_FILTERING, filterState)
      ? data?.filter((d) =>
          Object.keys(d)
            ?.filter(
              (k) =>
                !!filterState?.[k]?.length &&
                Object.keys(filterState ?? []).includes(k) &&
                MULTIPLE_FILTERING.includes(k),
            )
            ?.every((k) =>
              filterState?.[k]?.some((s) =>
                (d?.[k] as string[])?.includes(s?.value),
              ),
            ),
        )
      : data

  const filteredByTimebelt = hasFilterOn(["start", "end"], timebelt)
    ? data?.filter(
        (d) =>
          (d?.["timebelt"] as Record<string, string>)?.start >=
            (timebelt?.start ?? "") &&
          (d?.["timebelt"] as Record<string, string>)?.end <=
            (timebelt?.end ?? ""),
      )
    : data

  const filteredData = filtered_single
    ?.filter((fs) => filteredByTimebelt?.some((ft) => ft?.id === fs?.id))
    ?.filter((f) => filtered_multiple?.some((fm) => f?.id === fm?.id))

  const onFiltersSelect = (name: string, selectedOptions: Array<any>) =>
    setFilterState((c) => ({ ...c, [name]: selectedOptions }))

  const onTimebeltSelect = (hourInterval: HourInterval) => {
    setTimebelt(hourInterval)
  }

  return (
    <div
      className={css`
        padding: 1.5rem 0;
      `}
    >
      <Cable
        data={filteredData}
        id={baseRateId}
        columns={columns}
        filters={
          <BaseRateFilters
            categories={dimensions}
            data={data}
            timebelt={timebelt}
            selectedFilters={filterState}
            onFiltersSelect={onFiltersSelect}
            onTimebeltSelect={onTimebeltSelect}
          />
        }
        NewRow={
          open ? (
            <CreateNewRow
              data={data}
              baseRateId={baseRateId}
              dimensions={dimensions}
              getErrors={getErrors}
              targets={targets}
              onClose={handleClose}
              rowId={activeRow}
            />
          ) : null
        }
        onOpen={handleOpen}
        allowed={allowed}
        open={open}
        errors={errors}
      />
    </div>
  )
}

//#region CreateNewRow
function CreateNewRow(
  props: RatesTableProps & {
    targets?: MarketTarget[]
    onClose: () => void
    rowId?: string
  },
) {
  const { baseRateId, dimensions, getErrors, targets, onClose, data, rowId } =
    props
  const { t } = useI18n()

  const dms = getDimensionsKeys(dimensions)

  const getNewRowInitialState = () =>
    !!rowId
      ? (data
          .map((d) => {
            const targetB = targets?.find((t) => d?.targetB?.includes(t?.id))
            return { ...d, targetB }
          })
          .find((d) => d.id === rowId) ?? {})
      : Object.fromEntries(dms.map((x) => [x, ""]))

  const [newRowValues, setNewRowValues] = useState<
    Record<string, string | string[] | HourInterval | MarketTarget | undefined>
  >(getNewRowInitialState)

  const handleNewRowFieldChange: ChangeEventHandler<HTMLInputElement> = (
    evt,
  ) => {
    const { value, name } = evt.target
    setNewRowValues((s) => ({ ...s, [name]: value }))
    !!getErrors && getErrors()
  }

  const [mu] = useMutation(createBaseRateRow, {
    onSuccess: () => {
      setNewRowValues(getNewRowInitialState)
      queryCache.invalidateQueries("base-rate")
      onClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!getErrors && getErrors(errorMap)
    },
  })

  const invalid = Object.values(newRowValues).some((x) => {
    return typeof x === "string" ? !Boolean(x.trim()) : !Boolean(x)
  })

  const handleSubmitRow = () => {
    if (invalid) return
    const { targetB, ...rest } = newRowValues
    const row = {
      targetB: (newRowValues["targetB"] as MarketTarget)?.id,
      ...rest,
      [BREAKDOWN_SEMANTIC_CODE]: getInitialBreakdownValues(dimensions),
    }
    const pld = { baseRateId, row }
    mu(pld)
  }
  const storedOrder = useColumnKeys().slice(0, dms?.length)

  const span = Object.keys(getInitialBreakdownValues(dimensions)).length + 1

  useEffect(
    () => setNewRowValues(getNewRowInitialState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseRateId, rowId],
  )

  return (
    <tr>
      {dms
        .sort((a, b) => storedOrder?.indexOf(a) - storedOrder?.indexOf(b))
        .map((name) => {
          const value = newRowValues[name]
          const svalue = value as string
          const hvalue = value as HourInterval
          const nvalue = Array.isArray(value) ? value : []
          const tvalue = value as MarketTarget
          const options = DAYS_OF_WEEK.map((d) => ({
            name: t(`days.${d}`),
            value: d,
          }))
          const selected = nvalue?.map((d) => ({
            name: t(`days.${d}`),
            value: d,
          }))
          const inputValue = selected?.map((s) => s.name).join(", ")

          const targetOptions =
            targets
              ?.filter((t) => t?.status === "ACTIVE")
              ?.map(
                (t) =>
                  ({
                    value: t?.id,
                    label: t?.name,
                    disabled: t?.status !== "ACTIVE",
                  }) as IOption,
              ) || []

          const onToggleDay = (days: Array<Record<string, string>>) => {
            const v = days.map((d) => d.value)
            setNewRowValues((s) => ({ ...s, [name]: v }))
            !!getErrors && getErrors()
          }

          const onSelectAllDays = (doSelectAll: boolean) => {
            doSelectAll
              ? setNewRowValues((s) => ({ ...s, [name]: DAYS_OF_WEEK }))
              : setNewRowValues((s) => ({ ...s, [name]: [] }))
            !!getErrors && getErrors()
          }

          const onClearDays = () => {
            setNewRowValues((s) => ({ ...s, [name]: [] }))
            !!getErrors && getErrors()
          }

          const onTimeBeltChange = ({
            name,
            value,
          }: {
            name: string
            value?: string
          }) => {
            setNewRowValues((s) => ({
              ...s,
              timebelt: { ...(s?.timebelt as HourInterval), [name]: value },
            }))
            !!getErrors && getErrors()
          }

          const onTargetChange = (changes: SelectedOptionChanges<IOption>) => {
            const { value } = changes
            const target = targets?.find((t) => t?.id === value)
            setNewRowValues((s) => ({
              ...s,
              targetB: target ?? {},
            }))
            !!getErrors && getErrors()
          }

          return (
            <td
              key={name}
              className={css`
                border-right: 1px solid transparent !important;
              `}
            >
              <div className={styles.cellContentWrapper}>
                {name === "daysOfWeek" ? (
                  <MultiSelect
                    checkValues={() => true}
                    items={options}
                    getOptionLabel={(option: Record<string, string>) =>
                      option.name
                    }
                    selectedValues={selected || []}
                    renderOption={(option: Record<string, string>) =>
                      option?.name
                    }
                    onToggleOption={onToggleDay}
                    onClearOptions={onClearDays}
                    onSelectAll={onSelectAllDays}
                    selectAllValue="select-all"
                    selectAllOption={{
                      name: t("filters.all"),
                      value: "select-all",
                    }}
                    className={styles.multi}
                    inputClassName={styles.multiInput}
                    inputValue={inputValue}
                    placeholder={t(`daysOfWeek`)}
                    onKeyDown={(e) => e.preventDefault()}
                    margin="dense"
                  />
                ) : name === "timebelt" ? (
                  <HourRangePicker
                    start={hvalue?.start || ""}
                    end={hvalue?.end || ""}
                    onChange={onTimeBeltChange}
                    size="small"
                    inputLabelClass={styles.timebeltClass}
                  />
                ) : name === "targetB" ? (
                  <AutoCompleteInput<IOption>
                    value={targetOptions?.find(
                      (target) => target.value === tvalue?.id,
                    )}
                    size="small"
                    placeholder={t("rate.card.dimensions.targetB")}
                    fetchOnFilter={false}
                    getOptionLabel={(target) => target?.label || ""}
                    options={targetOptions}
                    getOptionSelected={(opt, val) => opt?.value === val?.value}
                    renderOption={(target) =>
                      target ? <span>{target.label}</span> : ""
                    }
                    onChange={(change) => {
                      !!change && onTargetChange(change)
                    }}
                    classes={{ root: styles.autoInput }}
                  />
                ) : (
                  <TextInput
                    name={name}
                    placeholder={t(name)}
                    value={svalue}
                    margin="dense"
                    size="small"
                    className={styles.tc}
                    onChange={handleNewRowFieldChange}
                  />
                )}
              </div>
            </td>
          )
        })}
      <td colSpan={span}>
        <ButtonGroup
          className={css`
            text-align: end;
          `}
        >
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitRow}
            disabled={invalid}
          >
            {t("actions.create")}
          </Button>
        </ButtonGroup>
      </td>
    </tr>
  )
}
//#endregion

//#region state utils
function getDimensionsKeys(dimensions: Dimension[]) {
  return dimensions
    .filter(
      (x) => x.enabled && x.mediaFieldSemanticCode !== BREAKDOWN_SEMANTIC_CODE,
    )
    .map((x) => x.mediaFieldSemanticCode)
}

function getInitialBreakdownValues(dimensions: Dimension[]) {
  const bdd = dimensions.find(
    (x) => x.mediaFieldSemanticCode === BREAKDOWN_SEMANTIC_CODE,
  )
  if (!bdd || !bdd.options) return {}
  return Object.fromEntries(bdd.options.map((x) => x.value).map((x) => [x, 0]))
}
//#endregion

export function computeDerivedDurationValues(
  baseValue: number,
  percentageValue: number,
) {
  return (baseValue * (percentageValue / 100)).toFixed(2)
}

//#region COLUMNS
function createColumns(
  dimensions: Dimension[],
  baseRateId: string,
  t: (key: string) => string,
  allowed: boolean,
  onOpen: VoidFunction,
  getErrors?: (errors?: ErrorsMap) => void,
  targets?: MarketTarget[],
) {
  // ** actions **
  const actionColumn: Column = {
    id: "actions",
    Header: t("actions"),
    width: 75,
    border: "1px solid var(--border-color-2)",
    fixed: true,
    default: true,
    cell: {
      type: "display",
      render: function ActionsCell(props) {
        return (
          <Actions
            rowId={props.rowId}
            baseRateId={baseRateId}
            getErrors={getErrors}
            onOpen={onOpen}
          />
        )
      },
    },
  }
  // ** dimensions
  const dimensionsColumns: Column[] = getDimensionsKeys(dimensions).map(
    (key) => {
      return {
        id: key,
        Header: t(key),
        width: 140,
        border: "1px solid var(--border-color-2)",
        default: true,
        cell: {
          path: key,
          type: "writable",
          value: (row: BaseRateRowRecord) => row[key] ?? "",
          render: function C(props) {
            let rendered = (
              <InputDimension
                {...props}
                baseRateId={baseRateId}
                getErrors={getErrors}
                allowed={allowed}
              />
            )
            switch (key) {
              case "daysOfWeek":
                rendered = (
                  <DaysOfWeekDimension
                    {...props}
                    baseRateId={baseRateId}
                    getErrors={getErrors}
                    allowed={allowed}
                  />
                )
                break
              case "timebelt":
                rendered = (
                  <div className={styles.timebeltWrapper}>
                    <TimeBeltDimension
                      {...props}
                      baseRateId={baseRateId}
                      getErrors={getErrors}
                      allowed={allowed}
                    />
                  </div>
                )
                break
              case "targetB":
                rendered = (
                  <TargetDimension
                    {...props}
                    baseRateId={baseRateId}
                    getErrors={getErrors}
                    allowed={allowed}
                    targets={targets}
                  />
                )
            }
            return rendered
          },
        },
      }
    },
  )

  // ** breakdown **
  const bdd = dimensions.find(
    (x) => x.mediaFieldSemanticCode === BREAKDOWN_SEMANTIC_CODE,
  )

  const column = allowed
    ? dimensionsColumns.concat([actionColumn])
    : dimensionsColumns

  // * If no breakdown return early
  if (!bdd || !bdd.options) return column

  const breakdownColumn = getBreakdownColumn(
    bdd.options,
    baseRateId,
    t,
    getErrors,
    allowed,
  )

  const allColumns = allowed
    ? dimensionsColumns.concat([breakdownColumn, actionColumn])
    : dimensionsColumns.concat([breakdownColumn])

  // * return all finally
  return allColumns
}

function getBreakdownColumn(
  options: DimensionOption[],
  baseRateId: string,
  t: (key: string) => string,
  getErrors?: (errors?: ErrorsMap) => void,
  allowed?: boolean,
): Column {
  const getPath = (x: string) => [BREAKDOWN_SEMANTIC_CODE, x].join(".")

  const tryToSortNumericColumnNames = (cs: Column[]) => {
    // eg for durations, 10, 20, ...
    const notNumerics = cs.some((c) => isNaN(parseInt(c.id)))
    if (notNumerics) return cs

    return [...cs].sort((a, b) => Number(a) - Number(b))
  }

  // * Manual entries
  const manualColumns: Column[] = options
    .filter((x) => x.type === "MANUAL")
    .map((x) => {
      const optionName = `${x.value}`
      const path = getPath(optionName)
      return {
        id: optionName,
        Header: optionName,
        width: 140,
        border: "1px solid var(--border-color-2)",
        fixed: true,
        default: true,
        cell: {
          path,
          type: "writable",
          value: (row: BaseRateRowRecord) => lget(row, path, 0),
          render: function C(props) {
            return (
              <InputDuration
                {...props}
                baseRateId={baseRateId}
                getErrors={getErrors}
                allowed={allowed}
              />
            )
          },
        },
      }
    })

  // * derived entries

  const defaultOption = options.find(
    (x) => x.type === "MANUAL" && Boolean(x.isDefault),
  )
  const hasPercentageColumns = options.some((x) => x.type === "PERCENTAGE")

  // If we have at least one percentage type option
  // but no default option we can't compute values
  // so return early
  if (!defaultOption && hasPercentageColumns) {
    return {
      id: BREAKDOWN_SEMANTIC_CODE,
      Header: t(BREAKDOWN_SEMANTIC_CODE),
      columns: tryToSortNumericColumnNames(manualColumns),
    }
  }

  const defaultColumnId = `${defaultOption!.value}`

  const derivedColumns: Column[] = options
    .filter((x) => x.type === "PERCENTAGE")
    .map((x) => {
      const optionName = `${x.value}`
      return {
        id: optionName,
        Header: optionName,
        width: 140,
        border: "1px solid var(--border-color-2)",
        default: true,
        cell: {
          path: getPath(optionName),
          type: "derived",
          value: (get) => {
            const baseValue = get<number>(defaultColumnId, 0)
            const percentageValue = x.percentage ?? 0
            return computeDerivedDurationValues(baseValue, percentageValue)
          },
          render: function D(props) {
            const { atom } = props
            const [value] = useCellValue(atom)
            return <DerivedDuration value={value} />
          },
        },
      }
    })

  const allColumns = manualColumns.concat(derivedColumns)
  return {
    id: BREAKDOWN_SEMANTIC_CODE,
    Header: t(BREAKDOWN_SEMANTIC_CODE),
    border: "1px solid var(--border-color-2)",
    columns: tryToSortNumericColumnNames(allColumns),
    fixed: true,
    default: true,
    isVisible: true,
  }
}

//#endregion

//#region Cells
function Actions(props: {
  rowId: string
  baseRateId: string
  getErrors?: (errors?: ErrorsMap) => void
  onOpen: (id?: string | undefined) => void
}) {
  const { t } = useI18n()
  const { rowId, baseRateId, getErrors, onOpen } = props
  const ro = useRow(rowId)
  //

  const [mu, result] = useMutation(deleteBaseRateRow, {
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!getErrors && getErrors(errorMap)
    },
  })

  const onDelete = () => {
    mu({ rowId, baseRateId }).then(() => {
      return ro.removeRow()
    })
  }

  const onDuplicate = () => {
    onOpen(rowId)
  }

  return (
    <MoreActions
      options={[
        // DUPLICATE
        {
          // hidden: hideDeactivate,
          label: t("actions.duplicate"),
          onClick: () => {
            onDuplicate()
          },
        },
        // DELETE
        {
          hidden: result.isLoading, //hideDelete,
          label: t("actions.delete"),
          onClick: () => {
            onDelete()
          },
          labelStyle: css`
            > span {
              color: var(--red);
            }
          `,
        },
      ]}
    />
  )
}

function DerivedDuration(props: { value: number }) {
  const c = formatFloatingPointNumber(3)(props.value)
  return <div className={styles.cellContentWrapper}>{c}</div>
}

function InputDuration(
  props: WritableCellRenderProps<number> & {
    baseRateId: string
    getErrors?: (errors?: ErrorsMap) => void
    allowed?: boolean
  },
) {
  const { atom, rowId, baseRateId, getErrors, allowed } = props
  const [value, setValue] = useCell(atom)

  const updateRow = useUpdateRow(rowId, baseRateId, getErrors)

  const onBlur = updateRow

  const rifm = useRifm({
    value: value + "",
    accept: /[\d.]/g,
    onChange: (v) => {
      setValue(+parseNumber(v))
      !!getErrors && getErrors()
    },
    format: formatFloatingPointNumber(3),
  })

  const onFocusSelect = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const inp = evt.target
    inp && inp.setSelectionRange(0, inp?.value?.length || 0)
  }

  return (
    <div className={styles.cellContentWrapper}>
      <Input
        value={rifm.value}
        onChange={rifm.onChange}
        onFocus={onFocusSelect}
        className={styles.tc}
        onBlur={onBlur}
        disabled={!allowed}
      />
    </div>
  )
}

function TimeBeltDimension(
  props: WritableCellRenderProps<HourInterval> & {
    baseRateId: string
    getErrors?: (errors?: ErrorsMap) => void
    allowed?: boolean
  },
) {
  const { atom, rowId, baseRateId, getErrors, allowed } = props
  const updateRow = useUpdateRow(rowId, baseRateId, getErrors)

  const [value, setValue] = useCell<HourInterval>(atom)
  /* just so that it won't break on old values */
  const nvalue = typeof value === "string" ? undefined : value

  const handleChange = ({ name, value }: { name: string; value?: string }) => {
    setValue((v) => ({ ...v, [name]: value }))
    !!getErrors && getErrors()
  }

  return allowed ? (
    <div className={styles.cellContentWrapper}>
      <HourRangePicker
        start={nvalue?.start || ""}
        end={nvalue?.end || ""}
        onChange={handleChange}
        onBlur={updateRow}
        size="small"
        disabled={!allowed}
      />
    </div>
  ) : (
    <div className={styles.hourRangeLine}>
      <span
        className={cx("BodyBody-2BlackRegular", styles.readonly)}
      >{`${hourLabelFromTime(nvalue?.start)}`}</span>
      <span className={styles.line} />
      <span
        className={cx("BodyBody-2BlackRegular", styles.readonly)}
      >{`${hourLabelFromTime(nvalue?.end)}`}</span>
    </div>
  )
}

function DaysOfWeekDimension(
  props: WritableCellRenderProps<string[]> & {
    baseRateId: string
    getErrors?: (errors?: ErrorsMap) => void
    allowed?: boolean
  },
) {
  const { atom, rowId, baseRateId, getErrors, allowed } = props
  const { t } = useI18n()
  const updateRow = useUpdateRow(rowId, baseRateId, getErrors)
  const onBlur = allowed ? updateRow : undefined

  const [value = [], setValue] = useCell<string[]>(atom)
  const [open, setOpen] = useState<boolean>(false)
  /* just so that it won't break on old values */
  const nvalue = typeof value === "string" ? [] : value
  const options = DAYS_OF_WEEK.map((d) => ({ name: t(`days.${d}`), value: d }))
  const selected = nvalue.map((d) => ({ name: t(`days.${d}`), value: d }))
  const inputValue = selected.map((s) => s.name).join(", ")

  const onToggleDay = (days: Array<Record<string, string>>) => {
    const v = days.map((d) => d.value)
    setValue(v)
    !!getErrors && getErrors()
  }

  const onSelectAllDays = (doSelectAll: boolean) => {
    doSelectAll ? setValue(DAYS_OF_WEEK) : setValue([])
    !!getErrors && getErrors()
  }

  const onClearDays = () => {
    setValue([])
    !!getErrors && getErrors()
  }

  const onSelectOpen = () => setOpen(true)
  const onSelectClose = () => setOpen(false)

  return (
    <div className={styles.cellContentWrapper}>
      <MultiSelect
        checkValues={() => true}
        items={options}
        getOptionLabel={(option: Record<string, string>) => option.name}
        selectedValues={selected || []}
        renderOption={(option: Record<string, string>) => option?.name}
        onToggleOption={onToggleDay}
        onClearOptions={onClearDays}
        onSelectAll={onSelectAllDays}
        selectAllValue="select-all"
        selectAllOption={{ name: t("filters.all"), value: "select-all" }}
        inputClassName={styles.multiInput}
        inputValue={inputValue}
        onBlur={onBlur}
        onOpen={onSelectOpen}
        open={open}
        onClose={onSelectClose}
        disabledCheckbox={!allowed}
        margin="dense"
      />
    </div>
  )
}

function InputDimension(
  props: WritableCellRenderProps<string> & {
    baseRateId: string
    getErrors?: (errors?: ErrorsMap) => void
    allowed?: boolean
  },
) {
  const { atom, rowId, baseRateId, getErrors, allowed } = props
  const updateRow = useUpdateRow(rowId, baseRateId, getErrors)
  const onBlur = updateRow

  const [value, setValue] = useCell<string>(atom)
  const handleChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    setValue(evt.target.value)
    !!getErrors && getErrors()
  }
  return (
    <div className={styles.cellContentWrapper}>
      <TextInput
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        margin="dense"
        size="small"
        className={styles.tc}
        disabled={!allowed}
      />
    </div>
  )
}

function TargetDimension(
  props: WritableCellRenderProps<string[] | undefined> & {
    baseRateId: string
    getErrors?: (errors?: ErrorsMap) => void
    allowed?: boolean
    targets?: MarketTarget[]
  },
) {
  const { atom, rowId, baseRateId, getErrors, allowed, targets } = props
  const [value, setValue] = useCell<string[] | undefined>(atom)
  const targetValue = value?.[0]
  const updateRow = useUpdateRow(rowId, baseRateId, getErrors)

  const handleChange = (changes: SelectedOptionChanges<IOption>) => {
    const { value } = changes
    const target = targets?.find((t) => t?.id === value)?.id
    if (target) {
      setValue([target])
      updateRow()
    }
    !!getErrors && getErrors()
  }

  const targetOptions =
    targets
      ?.filter((t) => t?.status === "ACTIVE")
      ?.map(
        (t) =>
          ({
            value: t?.id,
            label: t?.name,
            disabled: t?.status !== "ACTIVE",
          }) as IOption,
      ) || []

  return (
    <AutoCompleteInput<IOption>
      size="small"
      fetchOnFilter={false}
      getOptionLabel={(target) => target?.label || ""}
      value={targetOptions.find((target) => target.value === targetValue)}
      options={targetOptions}
      disabled={!allowed}
      getOptionSelected={(opt, val) => {
        return opt?.value === val?.value
      }}
      renderOption={(target) => (target ? <span>{target.label}</span> : "")}
      onChange={(change) => {
        if (change !== null) {
          handleChange(change)
        }
      }}
      className={styles.autoInput}
    />
  )
}

//

function useUpdateRow(
  rowId: string,
  baseRateId: string,
  getErrors?: (errors?: ErrorsMap) => void,
) {
  const ro = useRow<BaseRateRowRecord>(rowId)
  const [updateRowMutation] = useMutation(updateBaseRateRow, {
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!getErrors && getErrors(errorMap)
    },
  })

  return async () => {
    const { initial, cells } = await ro.getRowInfo(rowId)
    const new_row = cells.reduce((all, next) => {
      const [path, value] = next
      return setW(all, path, value, Object)
    }, {} as Partial<BaseRateRowRecord>)

    const row: BaseRateRowRecord = { ...initial, ...new_row }
    const pld = { baseRateId, row }
    return updateRowMutation(pld)
  }
}

//#endregion

const styles = {
  tc: css`
    text-align: center;
    &.MuiFormControl-marginDense {
      margin: 0;
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: transparent;
    }
    .MuiOutlinedInput-input {
      text-align: center;
    }
  `,
  cellContentWrapper: css`
    min-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  `,
  multi: css`
    .MuiFormControl-marginDense {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  multiInput: css`
    border-width: 0;
    .MuiOutlinedInput-input {
      text-overflow: ellipsis;
    }
    .MuiInputBase-root {
      font-size: 13px;
    }
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 0;
    }
    fieldset {
      border-width: 0;
    }
  `,
  hourRangeLine: css`
    display: flex;
    align-items: center;
  `,
  line: css`
    height: 0px;
    width: 12px;
    border-top: 1px solid black;
    color: var(--black);
    align-items: center;
    display: inline-flex;
    justify-content: center;
    margin: 0 8px;
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
  timebeltClass: css`
    font-size: 13px;
    transform: translate(10px, 10px) scale(1);
  `,
  timebeltWrapper: css`
    display: flex;
    justify-content: center;
  `,
  ghost: css`
    height: var(--spacing-xxl);
    align-items: center;
  `,
  autoInput: css`
    overflow: visible;
    .MuiSvgIcon-root {
      scale: 0.6;
    }
    .MuiInputBase-input {
      min-width: 80px;
    }
    label.MuiInputLabel-outlined.MuiInputLabel-shrink {
      transform: translate(13px, -5px) scale(0.7);
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: transparent;
    }
    .MuiIconButton-label {
      width: 18px;
      height: 18px;
      .MuiSvgIcon-root {
        scale: 0.65;
      }
    }
  `,
}
